import { Add } from "@mui/icons-material";
import { Card, Grid } from "@mui/material";
import { useEffect, useState } from "react";
import { Autocomplete, Box, Button, DataTable, useCommonComponentContext, IPropListContainer } from "@maysoft/common-component-react";

import Helpers from "../../commons/helpers";
import useStrings from "../../hooks/useStrings";
import TagService from "../../services/message/Tag.service";

import { Constants } from "../../constants/constants";
import { Mode, TagType } from "../../constants/enums";
import { ICodename, IMultiLanguageContent } from "../../commons/interfaces";

export interface IRecordTag {
    tenantCode?: string;
    serviceCode?: string;
    organizationId?: string;
    name?: IMultiLanguageContent;
    shortName?: IMultiLanguageContent;
    type?: number;
    id?: string;
    status?: number;
    createTime?: string;
    createUser?: string;
    updateTime?: string;
    updateUser?: string;
}

export interface IRequestGetpaged {
    type?: number;
    pageSize: number;
    pageNumber: number;
    totalCount: number;
}

interface IModel {
    requestData: IRequestGetpaged;
    requestDataTemp: IRequestGetpaged;
}

const TagListContainer = (props: IPropListContainer<IRequestGetpaged>) => {
    const Strings = useStrings();
    const { language, tenantCode, serviceCode, onError, onSuccess, onHideLoading, onShowLoading } = useCommonComponentContext();

    const [model, setModel] = useState<IModel>({} as IModel);

    const dataType: ICodename[] = [
        { code: TagType.Post, name: Strings.TAG.POST },
        { code: TagType.Course, name: Strings.TAG.COURSE },
        { code: TagType.Expertised, name: Strings.TAG.EXPERTISED },
    ];

    const [dataRows, setDataRows] = useState<IRecordTag[]>([]);
    const dataColumns: any[] = [
        {
            Header: Strings.TAG.TAG_NAME,
            accessor: "name",
            width: "auto",
            Cell: ({ value }: any) => <>{Helpers.renderValueByLanguage({ data: value, currentLanguage: language })}</>,
        },
        {
            Header: Strings.TAG.SHORT_NAME,
            accessor: "shortName",
            width: "auto",
            Cell: ({ value }: any) => <>{Helpers.renderValueByLanguage({ data: value, currentLanguage: language })}</>,
        },
        {
            Header: Strings.TAG.SEQUENCE,
            accessor: "sequence",
            width: "auto",
            Cell: ({ value }: any) => <>{value}</>,
        },
        {
            Header: Strings.TAG.TYPE_TAG,
            accessor: "type",
            width: "auto",
            Cell: ({ value }: any) => <>{dataType.find((el) => el.code === value)?.name || ""}</>,
        },
        // {
        //     Header: Strings.Common.STATUS, accessor: "status", width: "auto",
        //     Cell: ({ value }: any) => <CustomStatusCell status={value} />,
        // },
    ];

    useEffect(() => {
        const newDataReq: IRequestGetpaged = {
            totalCount: props.requestGetPaged?.totalCount || 0,
            pageSize: props.requestGetPaged?.pageSize || model?.requestData?.pageSize,
            pageNumber: props.requestGetPaged?.pageNumber || model?.requestData?.pageNumber,

            type: props.requestGetPaged?.type || model?.requestData?.type,
        };

        getPagedTag(newDataReq);
    }, [props.requestGetPaged]);

    const getPagedTag = async (dataReq?: IRequestGetpaged) => {
        try {
            onShowLoading();

            const pageSize = dataReq?.pageSize || Constants.ROW_PER_PAGE;
            const pageNumber = Helpers.getPageNumber(dataReq?.pageNumber || 1, pageSize, dataReq?.totalCount || 0);

            const newReq = {
                pageSize: pageSize,
                pageNumber: pageNumber,
                tenantCode: tenantCode,
                serviceCode: serviceCode,
                type: Helpers.isNullOrEmpty(dataReq?.type) ? undefined : Number(dataReq?.type),
            };

            const result = await TagService.getPaged(newReq);

            setDataRows([...(result?.items || [])]);
            setModel({
                requestData: {
                    ...newReq,
                    totalCount: result.totalCount,
                },
                requestDataTemp: {
                    ...newReq,
                    totalCount: result.totalCount,
                },
            });

            let query: string = `?pageNumber=${newReq?.pageNumber}&pageSize=${newReq?.pageSize}`;
            if (!Helpers.isNullOrEmpty(newReq?.type)) {
                query += `&type=${newReq?.type}`;
            }

            props.onGetPaged({ query, totalCount: result.totalCount || 0 });
        } catch (error: any) {
            onHideLoading();
            const err = Helpers.renderExceptionError(error);
            onError(err);
        } finally {
            onHideLoading();
        }
    };

    const handleDeleteTag = (id: string) => {
        Helpers.showConfirmAlert(Strings.TAG.CONFIRM_DELETE, async () => {
            try {
                onShowLoading();

                await TagService.delete(id);

                await getPagedTag({ ...model?.requestData });

                onSuccess(Strings.TAG.DELETE_SUCCESS);
            } catch (error) {
                onHideLoading();
                const err = Helpers.renderExceptionError(error);
                onError(err);
            } finally {
                onHideLoading();
            }
        });
    };

    const filterForm = () => {
        return (
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Autocomplete
                        data={dataType || []}
                        label={Strings.TAG.TYPE_TAG}
                        placeholder={Strings.TAG.SELECT_TYPE_TAG}
                        defaultValue={model?.requestDataTemp?.type}
                        onChange={(value: any) => {
                            setModel({
                                ...model,
                                requestDataTemp: {
                                    ...model.requestDataTemp,
                                    type: value,
                                },
                            });
                        }}
                    />
                </Grid>
            </Grid>
        );
    };

    return (
        <Box>
            <Box justifyContent="flex-end" display="flex" mb={2}>
                <Button onClick={() => props.onNavigate({ mode: Mode.Create })}>
                    <Add /> &nbsp; {Strings.Common.ADD_NEW}
                </Button>
            </Box>
            <Card>
                <Box p={2}>
                    <DataTable
                        isSorted
                        rowPerPage={model?.requestData?.pageSize}
                        pageNumber={model?.requestData?.pageNumber}
                        totalCount={model?.requestData?.totalCount}
                        onChangePageSize={(pageSize) => {
                            getPagedTag({ ...model?.requestData, pageSize });
                        }}
                        onChangePageNumber={(pageNumber) => {
                            getPagedTag({ ...model?.requestData, pageNumber });
                        }}
                        filterForm={filterForm()}
                        onFilter={() => {
                            getPagedTag({ ...model?.requestDataTemp, pageNumber: 1 });
                        }}
                        onReset={() => {
                            getPagedTag();
                        }}
                        onCloseFilter={() => {
                            const temp = { ...model.requestData };
                            setModel({
                                ...model,
                                requestDataTemp: temp,
                            });
                        }}
                        table={{
                            rows: dataRows,
                            columns: dataColumns,
                        }}
                        actionList={[
                            {
                                key: "detail",
                                iconName: "view",
                                title: Strings.Common.VIEW_DETAIL,
                                callback: (row) => {
                                    props.onNavigate({ id: row.id, mode: Mode.View });
                                },
                            },
                            {
                                key: "edit",
                                iconName: "edit",
                                title: Strings.Common.EDIT,
                                callback: (row) => {
                                    props.onNavigate({ id: row.id, mode: Mode.Update });
                                },
                            },
                            {
                                key: "delete",
                                iconName: "delete",
                                title: Strings.Common.DELETE,
                                callback: (row) => handleDeleteTag(row.id),
                            },
                        ]}
                    />
                </Box>
            </Card>
        </Box>
    );
};

export default TagListContainer;
